<script setup lang="ts">
// markstream custom component for a Slack `<@U…>` user mention, emitted
// by slackMarkdown.ts as `<mention id="U087" name="Algot"></mention>`.
// Renders an inline chip: a small round avatar (resolved from the
// per-conversation participant directory) + the @name. The id is the
// bare Slack user id; `participants` is keyed by that id.
//
// Resolution order for the display name: directory name -> the inline
// `name` attr (Slack's own fallback label) -> 'user'. Avatars are
// same-origin only (the directory only ever holds Trove-proxied URLs);
// when there's no avatar we draw an initial-letter mark, mirroring
// AppNav's identity-mark treatment.
import { computed } from 'vue';
import { participants } from '@hq/chat-ui';
import { wsUrl } from '@/lib/workspace';
import { attr } from './nodeAttrs';

const props = defineProps<{ node: any }>();

const id = computed(() => attr(props.node, 'id'));
const fallbackName = computed(() => attr(props.node, 'name'));

const person = computed(() => (id.value ? participants.value[id.value] : undefined));

const name = computed(() => person.value?.name || fallbackName.value || 'user');
const avatar = computed(() => wsUrl(person.value?.avatar_url || ''));
const initial = computed(() => (name.value.trim()[0] || '?').toUpperCase());
</script>

<template>
  <span class="mention-chip" :title="`@${name}`">
    <span class="mention-chip__avatar" aria-hidden="true">
      <img v-if="avatar" :src="avatar" alt="" />
      <template v-else>{{ initial }}</template>
    </span>
    <span class="mention-chip__name">@{{ name }}</span>
  </span>
</template>

<style scoped>
.mention-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 7px 1px 2px;
  border-radius: var(--radius-pill);
  background: var(--surface-sunken);
  color: var(--ink-primary);
  font-size: 0.9em;
  line-height: 1.4;
  vertical-align: baseline;
  white-space: nowrap;
}
.mention-chip__avatar {
  flex-shrink: 0;
  width: 1.15em;
  height: 1.15em;
  border-radius: var(--radius-pill);
  background: var(--ink-subtle);
  color: var(--surface-canvas);
  display: grid;
  place-items: center;
  overflow: hidden;
  font-family: var(--font-mono);
  font-size: 0.7em;
  font-weight: 700;
}
.mention-chip__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.mention-chip__name {
  font-weight: 600;
}
</style>
